import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Paper,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useTheme, useMediaQuery } from "@mui/material";
import { getAuthToken } from "../../../utils/getAuthToken";
import TemplateAnalyticsPreview from "./TemplateAnalyticsFeatures/TemplateAnalyticsPreview";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { deleteTemplate, clearDeleteState, fetchTemplateForEdit, setActiveStep } from "../../../redux/Slice/Templates/templateSlice";
import { startEditTemplate } from "../../../redux/Slice/Templates/templateCreationSlice";
import { buildEditFormState } from "../../../utils/templateEditFormState";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

const COLORS = ["#2F8D6B", "#8884d8", "#82ca9d", "#ffc658", "#ff7300", "#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

const TemplateAnalytics = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));
  const isLg = useMediaQuery(theme.breakpoints.only('lg'));
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountid } = useParams();

  // Select only the name to prevent re-renders when other template properties change
  const templateNameFromRedux = useSelector((state) => state.template.selectedTemplate?.name);
  const templateCategoryFromRedux = useSelector((state) => state.template.selectedTemplate?.category);
  const templateGraphIdFromRedux = useSelector((state) => state.template.selectedTemplate?.id);
  const deleteStatus = useSelector((state) => state.template.deleteStatus);
  const deleteError = useSelector((state) => state.template.deleteError);
  const canDeleteTemplates = useSelector((state) => state.userDetails?.userPermissions?.permissions?.Templates?.Add) || false;
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editUnsupportedMsg, setEditUnsupportedMsg] = useState("");
  
  // Calculate chart height based on breakpoint
  const chartHeight = isXs ? 150 : isSm ? 180 : isMd ? 200 : isLg ? 220 : 220;

  // Tighter plot area on xs: less empty space left of Y-axis and above legend
  const selectedRangeChartMargin = isXs
    ? { top: 4, right: 8, left: -20, bottom: 0 }
    : { top: 20, right: 30, left: 0, bottom: 0 };
  const selectedRangeLegendStyle = isXs ? { marginTop: -12, paddingLeft: 0 } : undefined;

  // Default to last 30 days - memoize initial dates to prevent recreation on every render
  const initialDates = useMemo(() => ({
    today: dayjs(),
    monthAgo: dayjs().subtract(29, 'day')
  }), []);
  
  // Memoize today for maxDate prop to prevent unnecessary re-renders
  const today = useMemo(() => dayjs(), []);
  
  const [startDate, setStartDate] = useState(initialDates.monthAgo);
  const [endDate, setEndDate] = useState(initialDates.today);

  // Memoize date values as strings for dependency comparison
  const startDateStr = useMemo(() => startDate?.format('YYYY-MM-DD'), [startDate]);
  const endDateStr = useMemo(() => endDate?.format('YYYY-MM-DD'), [endDate]);
  const templateName = useMemo(() => templateNameFromRedux, [templateNameFromRedux]);

  // Use refs to track previous values and prevent unnecessary updates
  const fetchingRef = useRef(false);
  const lastFetchParamsRef = useRef({ templateName: null, startDateStr: null, endDateStr: null });
  const previousDataHashRef = useRef(null);

  useEffect(() => {
    if (!templateName || !startDate || !endDate) return;
    
    // Check if we're already fetching the same data (use string comparison to avoid dayjs object reference issues)
    if (
      fetchingRef.current ||
      (lastFetchParamsRef.current.templateName === templateName &&
       lastFetchParamsRef.current.startDateStr === startDateStr &&
       lastFetchParamsRef.current.endDateStr === endDateStr)
    ) {
      return;
    }
    
    fetchingRef.current = true;
    lastFetchParamsRef.current = { templateName, startDateStr, endDateStr };
    setLoading(true);
    setError(null);
    
    // Capture date values to avoid closure issues
    const startDateValue = startDate;
    const endDateValue = endDate;
    
    // Get token from memory (not localStorage) - async function inside useEffect
    const fetchData = async () => {
      try {
        const token = await getAuthToken();
        if (!token) {
          setError("No token found");
          setLoading(false);
          fetchingRef.current = false;
          return;
        }
        const startUnix = startDateValue.startOf('day').unix();
        const endUnix = endDateValue.endOf('day').unix();
        const res = await axios.get(
          `${SERVER_DOMAIN}/api/templates/getanalytics?name=${encodeURIComponent(templateName)}&start=${startUnix}&end=${endUnix}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        // Create a hash of the data to compare
        const dataHash = JSON.stringify(res.data);
        
        // Only update if data has actually changed
        if (previousDataHashRef.current !== dataHash) {
          previousDataHashRef.current = dataHash;
          setAnalytics(res.data);
        }
        setLoading(false);
        fetchingRef.current = false;
      } catch (err) {
        setError(err.response?.data?.error || "Failed to fetch analytics");
        setLoading(false);
        fetchingRef.current = false;
      }
    };
    
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateName, startDateStr, endDateStr]);

  // Memoize chart data with stable references - only update when analytics object reference changes
  // Since we're preventing unnecessary analytics updates, this should be stable
  const chartData = useMemo(() => {
    return analytics?.chart || null;
  }, [analytics]);
  
  const clicksChartData = useMemo(() => {
    return analytics?.clicksChart || null;
  }, [analytics]);
  
  const uniqueBodies = useMemo(() => {
    return analytics?.uniqueBodies || null;
  }, [analytics]);

  const handleDeleteConfirm = () => {
    if (!templateName) return;
    dispatch(deleteTemplate(templateName));
  };

  // Fetches the template with its example values intact, reverse-maps it into the
  // Create Template wizard's form shape (templateEditFormState.js), and opens that
  // wizard at step 2 (body editor) pre-filled instead of building a separate edit UI.
  const handleEditClick = async () => {
    if (!templateGraphIdFromRedux) return;
    setEditUnsupportedMsg("");
    setEditLoading(true);
    try {
      const fullTemplate = await dispatch(fetchTemplateForEdit(templateGraphIdFromRedux)).unwrap();
      const { supported, reason, formState } = buildEditFormState(fullTemplate);
      if (!supported) {
        setEditUnsupportedMsg(reason);
        setEditLoading(false);
        return;
      }
      dispatch(startEditTemplate(formState));
      dispatch(setActiveStep(1));
      navigate(`/home/${accountid}/templates/create`);
    } catch (err) {
      setEditUnsupportedMsg(typeof err === "string" ? err : "Failed to load template for editing.");
      setEditLoading(false);
    }
  };

  // Navigate back to the templates list once the delete request succeeds
  useEffect(() => {
    if (deleteStatus === "succeeded") {
      setDeleteDialogOpen(false);
      dispatch(clearDeleteState());
      navigate(`/home/${accountid}/templates`);
    }
  }, [deleteStatus, dispatch, navigate, accountid]);

  return (
    <Box sx={{ 
      mx: "auto", 
      p: { xs: 1, sm: 2, md: 2.5, lg: 3, xl: 3 }, 
      backgroundColor: (theme) => theme.palette.background.default, 
      minHeight: { xs: 0, sm: 0, md: "100vh", lg: "100vh", xl: "100vh" },
      height: { xs: "100%", sm: "100%", md: "auto", lg: "auto", xl: "auto" },
      paddingBottom: { xs: "56px", sm: "62px", md: 0, lg: 0, xl: 0 },
      overflowY: { xs: "auto", sm: "auto", md: "visible", lg: "visible", xl: "visible" },
      overflowX: "hidden",
      scrollbarWidth: { xs: "none", sm: "none", md: "auto", lg: "auto", xl: "auto" },
      msOverflowStyle: { xs: "none", sm: "none", md: "auto", lg: "auto", xl: "auto" },
      "&::-webkit-scrollbar": {
        width: { xs: 0, sm: 0, md: "8px", lg: "8px", xl: "8px" },
      },
    }}>
      <Grid container spacing={{ xs: 1, sm: 2, md: 2.5, lg: 3, xl: 3 }}>
        {/* Left: Template Preview Card */}
        <Grid item xs={12} md={4}>
          <TemplateAnalyticsPreview />
        </Grid>
        {/* Right: Analytics */}
        <Grid item xs={12} md={8}>
          {/* Date Range Picker Row: date range on the left (next to the preview), delete on the right */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row', md: 'row' },
              justifyContent: { xs: 'center', sm: 'space-between', md: 'space-between' },
              alignItems: { xs: 'center', sm: 'center', md: 'center' },
              mb: { xs: 2, sm: 2, md: 2 },
              flexWrap: 'wrap',
              gap: { xs: 1.5, sm: 2, md: 2 },
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row', md: 'row' },
                  alignItems: { xs: 'center', sm: 'center', md: 'center' },
                  flexWrap: 'wrap',
                  gap: { xs: 1.5, sm: 2, md: 2 },
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: (theme) => theme.palette.mode === 'dark' ? '#fff' : 'inherit',
                    mr: { xs: 0, sm: 1, md: 1, lg: 1, xl: 1 },
                    fontSize: { xs: "11px", sm: "12px", md: "14px", lg: "16px", xl: "16px" },
                    display: { xs: "none", sm: "block", md: "block", lg: "block", xl: "block" },
                  }}
                >
                  Date Range:
                </Typography>
                <DatePicker
                  label="Start date"
                  value={startDate}
                  maxDate={endDate}
                  onChange={(newValue) => newValue && setStartDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        width: { xs: "100%", sm: "auto", md: "auto", lg: "auto", xl: "auto" },
                        mt: { xs: 1, sm: 0, md: 0, lg: 0, xl: 0 },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "11px", sm: "12px", md: "14px", lg: "14px", xl: "14px" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "11px", sm: "12px", md: "14px", lg: "14px", xl: "14px" },
                          padding: { xs: "8px 32px 8px 12px", sm: "10px 32px 10px 14px", md: "10px 32px 10px 14px", lg: "10px 32px 10px 14px", xl: "10px 32px 10px 14px" },
                        },
                      }
                    }
                  }}
                />
                <DatePicker
                  label="End date"
                  value={endDate}
                  minDate={startDate}
                  maxDate={today}
                  onChange={(newValue) => newValue && setEndDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        width: { xs: "100%", sm: "auto", md: "auto", lg: "auto", xl: "auto" },
                        "& .MuiInputLabel-root": {
                          fontSize: { xs: "11px", sm: "12px", md: "14px", lg: "14px", xl: "14px" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "11px", sm: "12px", md: "14px", lg: "14px", xl: "14px" },
                          padding: { xs: "8px 32px 8px 12px", sm: "10px 32px 10px 14px", md: "10px 32px 10px 14px", lg: "10px 32px 10px 14px", xl: "10px 32px 10px 14px" },
                        },
                      }
                    }
                  }}
                />
              </Box>
            </LocalizationProvider>
            {canDeleteTemplates && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={editLoading ? <CircularProgress size={14} /> : <EditOutlinedIcon />}
                  disabled={!templateName || templateCategoryFromRedux === 'AUTHENTICATION' || editLoading}
                  title={templateCategoryFromRedux === 'AUTHENTICATION' ? 'Authentication templates cannot be edited' : undefined}
                  onClick={handleEditClick}
                  sx={{
                    textTransform: 'none',
                    fontSize: { xs: "11px", sm: "12px", md: "14px", lg: "14px", xl: "14px" },
                    whiteSpace: 'nowrap',
                  }}
                >
                  Edit Template
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteOutlineIcon />}
                  disabled={!templateName}
                  onClick={() => setDeleteDialogOpen(true)}
                  sx={{
                    textTransform: 'none',
                    fontSize: { xs: "11px", sm: "12px", md: "14px", lg: "14px", xl: "14px" },
                    whiteSpace: 'nowrap',
                  }}
                >
                  Delete Template
                </Button>
              </Box>
            )}
          </Box>

          {/* Shown when a template can't be opened in the edit wizard (e.g. carousel,
              unsupported button types) instead of silently mis-mapping it */}
          <Snackbar
            open={Boolean(editUnsupportedMsg)}
            autoHideDuration={6000}
            onClose={() => setEditUnsupportedMsg("")}
          >
            <Alert severity="error" onClose={() => setEditUnsupportedMsg("")} sx={{ width: '100%' }}>
              {editUnsupportedMsg}
            </Alert>
          </Snackbar>

          {/* Delete confirmation dialog */}
          <Dialog open={deleteDialogOpen} onClose={() => !( deleteStatus === 'loading') && setDeleteDialogOpen(false)}>
            <DialogTitle>Delete Template</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete the template "{templateName}"? This will permanently remove it from WhatsApp and cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteStatus === 'loading'}>
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                color="error"
                variant="contained"
                disabled={deleteStatus === 'loading'}
                startIcon={deleteStatus === 'loading' ? <CircularProgress size={16} color="inherit" /> : null}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete error feedback */}
          <Snackbar
            open={deleteStatus === 'failed'}
            autoHideDuration={6000}
            onClose={() => dispatch(clearDeleteState())}
          >
            <Alert severity="error" onClose={() => dispatch(clearDeleteState())} sx={{ width: '100%' }}>
              {deleteError || 'Failed to delete template.'}
            </Alert>
          </Snackbar>
          {/* Analytics Content with scroll if overflow */}
          <Box sx={{ maxHeight: { xs: 'none', md: 'calc(100vh - 200px)' }, overflowY: 'auto', pr: 1 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : analytics ? (
              <>
                

                {/* New: Summary boxes for Clicks, Sent, Delivered, Read */}
                <Grid container spacing={{ xs: 1.5, sm: 1.5, md: 2, lg: 2, xl: 2 }} sx={{ mb: { xs: 1.5, sm: 1.5, md: 2, lg: 2, xl: 2 } }}>
                  <Grid item xs={6} md={2.4}>
                    <Paper sx={{ 
                      p: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 }, 
                      textAlign: "center", 
                      borderRadius: 2, 
                      backgroundColor: (theme) => theme.palette.background.paper 
                    }}>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "14px", xl: "14px" } }}
                      >
                        Replies
                      </Typography>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mt: { xs: 0.5, sm: 1, md: 1, lg: 1, xl: 1 }, 
                          fontWeight: 700,
                          fontSize: { xs: "14px", sm: "16px", md: "18px", lg: "20px", xl: "20px" }
                        }}
                      >
                        {analytics.clicks}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Paper sx={{ 
                      p: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 }, 
                      textAlign: "center", 
                      borderRadius: 2, 
                      backgroundColor: (theme) => theme.palette.background.paper 
                    }}>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "14px", xl: "14px" } }}
                      >
                        Sent
                      </Typography>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mt: { xs: 0.5, sm: 1, md: 1, lg: 1, xl: 1 }, 
                          fontWeight: 700,
                          fontSize: { xs: "14px", sm: "16px", md: "18px", lg: "20px", xl: "20px" }
                        }}
                      >
                        {analytics.sent}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Paper sx={{ 
                      p: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 }, 
                      textAlign: "center", 
                      borderRadius: 2, 
                      backgroundColor: (theme) => theme.palette.background.paper 
                    }}>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "14px", xl: "14px" } }}
                      >
                        Delivered
                      </Typography>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mt: { xs: 0.5, sm: 1, md: 1, lg: 1, xl: 1 }, 
                          fontWeight: 700,
                          fontSize: { xs: "14px", sm: "16px", md: "18px", lg: "20px", xl: "20px" }
                        }}
                      >
                        {analytics.delivered}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Paper sx={{ 
                      p: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 }, 
                      textAlign: "center", 
                      borderRadius: 2, 
                      backgroundColor: (theme) => theme.palette.background.paper 
                    }}>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "14px", xl: "14px" } }}
                      >
                        Read
                      </Typography>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mt: { xs: 0.5, sm: 1, md: 1, lg: 1, xl: 1 }, 
                          fontWeight: 700,
                          fontSize: { xs: "14px", sm: "16px", md: "18px", lg: "20px", xl: "20px" }
                        }}
                      >
                        {analytics.read}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Paper sx={{ 
                      p: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 }, 
                      textAlign: "center", 
                      borderRadius: 2, 
                      backgroundColor: (theme) => theme.palette.background.paper 
                    }}>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "14px", xl: "14px" } }}
                      >
                        Failed
                      </Typography>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mt: { xs: 0.5, sm: 1, md: 1, lg: 1, xl: 1 }, 
                          fontWeight: 700,
                          fontSize: { xs: "14px", sm: "16px", md: "18px", lg: "20px", xl: "20px" }
                        }}
                      >
                        {analytics.failed||0}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Paper sx={{ 
                  p: { xs: "12px 8px 12px 0px", sm: 2, md: 2.5, lg: 3, xl: 3 }, 
                  mt: { xs: 2, sm: 2, md: 2.5, lg: 3, xl: 3 }, 
                  backgroundColor: (theme) => theme.palette.background.paper 
                }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ 
                      fontSize: { xs: "14px", sm: "16px", md: "18px", lg: "20px", xl: "20px" },
                      mb: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 },
                      pl: { xs: 1.25, sm: 0, md: 0, lg: 0, xl: 0 },
                    }}
                  >
                    Performance (Selected Range)
                  </Typography>
                  <Box sx={{ width: "100%", height: chartHeight, minHeight: chartHeight }}>
                    <ResponsiveContainer width="100%" height="100%" key={`chart-${templateName}-${startDateStr}-${endDateStr}`}>
                      <LineChart data={chartData || []} margin={selectedRangeChartMargin}>
                        <XAxis dataKey="date" tick={isXs ? { fontSize: 10 } : undefined} tickMargin={isXs ? 4 : undefined} />
                        <YAxis allowDecimals={false} width={isXs ? 34 : undefined} tick={isXs ? { fontSize: 10 } : undefined} />
                        <Tooltip />
                        <Legend wrapperStyle={selectedRangeLegendStyle} />
                        <Line type="monotone" dataKey="sent" stroke="#8884d8" name="Sent" strokeWidth={2} dot={false} isAnimationActive={false} />
                        <Line type="monotone" dataKey="delivered" stroke="#82ca9d" name="Delivered" strokeWidth={2} dot={false} isAnimationActive={false} />
                        <Line type="monotone" dataKey="read" stroke="#ffc658" name="Read" strokeWidth={2} dot={false} isAnimationActive={false} />
                        <Line type="monotone" dataKey="failed" stroke="#e53935" name="Failed" strokeWidth={2} dot={false} isAnimationActive={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>

                <Paper sx={{ 
                  p: { xs: "12px 8px 12px 0px", sm: 2, md: 2.5, lg: 3, xl: 3 }, 
                  mt: { xs: 2, sm: 2, md: 2.5, lg: 3, xl: 3 }, 
                  backgroundColor: (theme) => theme.palette.background.paper 
                }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ 
                      fontSize: { xs: "14px", sm: "16px", md: "18px", lg: "20px", xl: "20px" },
                      mb: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 },
                      pl: { xs: 1.25, sm: 0, md: 0, lg: 0, xl: 0 },
                    }}
                  >
                    Button Clicks (Selected Range)
                  </Typography>
                  <Box sx={{ width: "100%", height: chartHeight, minHeight: chartHeight }}>
                    <ResponsiveContainer width="100%" height="100%" key={`clicks-${templateName}-${startDateStr}-${endDateStr}`}>
                      <LineChart data={clicksChartData || []} margin={selectedRangeChartMargin}>
                        <XAxis dataKey="date" tick={isXs ? { fontSize: 10 } : undefined} tickMargin={isXs ? 4 : undefined} />
                        <YAxis allowDecimals={false} width={isXs ? 34 : undefined} tick={isXs ? { fontSize: 10 } : undefined} />
                        <Tooltip />
                        <Legend wrapperStyle={selectedRangeLegendStyle} />
                        {uniqueBodies && uniqueBodies.length > 0 ? (
                          uniqueBodies.map((body, idx) => (
                            <Line
                              key={body}
                              type="monotone"
                              dataKey={body}
                              stroke={COLORS[idx % COLORS.length]}
                              name={body}
                              strokeWidth={2}
                              dot={false}
                              isAnimationActive={false}
                            />
                          ))
                        ) : (
                          // Show placeholder lines when no unique bodies
                          <Line type="monotone" dataKey="clicks" stroke={COLORS[0]} name="Clicks" strokeWidth={2} dot={false} isAnimationActive={false} />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </>
            ) : null}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TemplateAnalytics; 